import { HStack, IconButton, Text, Flex, Box, Menu, MenuButton, MenuList, MenuItem, Avatar, Divider, Button, Breadcrumb, BreadcrumbItem, BreadcrumbLink, useColorMode, VStack, AlertDialog, AlertDialogBody, AlertDialogFooter, AlertDialogHeader, AlertDialogContent, AlertDialogOverlay } from '@chakra-ui/react'
import { Menu as MenuIcon, Bell, Settings, LogOut, User, Moon, Sun, Circle, Clock, Coffee, UserCheck } from 'lucide-react'
import { User as AppUser } from '@/types'
import { useLocation, Link } from 'react-router-dom'
import { useThemeColors } from '@/hooks/useThemeColors'
import { useSiteSettings } from '@/hooks/useSiteSettings'
import { useUserStatusStore } from '@/store/userStatus'
import { useAuthStore } from '@/store/auth'
import { useMemo, useRef, useState, useEffect } from 'react'
import { isTeamLead } from '@/lib/utils/roleHelper'
import { useToastNotification } from '@/lib/utils/useToastNotification'

interface HeaderProps {
  onOpen: () => void
  user: AppUser | null
  onLogout: () => void
}

export function Header({ onOpen, user, onLogout }: HeaderProps) {
  const colors = useThemeColors()
  const location = useLocation()
  const pathname = location.pathname
  const segments = (pathname || '/').split('/').filter(Boolean)
  const toLabel = (s: string) => s.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
  const title = segments.length > 0 ? toLabel(segments[0]) : 'Dashboard'
  const crumbs = segments.map((seg, idx) => ({
    label: toLabel(seg),
    href: '/' + segments.slice(0, idx + 1).join('/'),
  }))
  const { colorMode, toggleColorMode } = useColorMode()
  const userStatus = useUserStatusStore((s) => s.status)
  const statusChangedAt = useUserStatusStore((s) => s.statusChangedAt)
  const setUserStatus = useUserStatusStore((s) => s.setStatus)
  const [isLogoutOpen, setIsLogoutOpen] = useState(false)
  const cancelRef = useRef<HTMLButtonElement>(null)
  const authUser = useAuthStore((s) => s.user)
  const userIsTeamLead = isTeamLead(authUser)
  const { showError, showSuccess } = useToastNotification()

  // Auto-switch to online after break/lunch expires (not for offline)
  useEffect(() => {
    if (!statusChangedAt || userStatus === 'online' || userStatus === 'offline') return

    const checkInterval = setInterval(() => {
      const changedAt = new Date(statusChangedAt)
      const now = new Date()
      const elapsedMinutes = Math.floor((now.getTime() - changedAt.getTime()) / (1000 * 60))

      const durationMinutes = userStatus === 'lunch' ? 60 : 15 // lunch: 60min, break: 15min

      if (elapsedMinutes >= durationMinutes) {
        setUserStatus('online', (error) => {
          showError(error)
        })
      }
    }, 60000) // Check every minute

    return () => clearInterval(checkInterval)
  }, [statusChangedAt, userStatus, setUserStatus, showError])

  // Avatar background color based on user status
  const avatarBgColor = useMemo(() => {
    switch (userStatus) {
      case 'online':
        return '#22C55E' // Green for online
      case 'break':
        return '#F59E0B' // Amber/yellow for break
      case 'lunch':
        return '#EF4444' // Red/orange for lunch
      case 'offline':
        return '#6B7280' // Gray for offline
      default:
        return colors.primary
    }
  }, [userStatus, colors.primary])
  
  return (
    <Box bg={colors.cardBg} borderBottom="1px" borderColor={colors.border} px={6} py={4}>
      <Flex justify="space-between" align="center">
        <HStack align="start" spacing={3}>
          <IconButton
            aria-label="Open menu"
            icon={<MenuIcon />}
            variant="ghost"
            display="inline-flex"
            onClick={onOpen}
          />
          <Box display={{ base: 'none', md: 'block' }}>
            <Text fontSize="lg" fontWeight="semibold">{title}</Text>
            {segments.length > 0 && (
              <Breadcrumb fontSize="sm" color={colors.text} separator="/">
                <BreadcrumbItem key="home">
                  <BreadcrumbLink as={Link} to="/dashboard">Home</BreadcrumbLink>
                </BreadcrumbItem>
                {crumbs.map((c, i) => (
                  <BreadcrumbItem key={c.href} isCurrentPage={i === crumbs.length - 1}>
                    {i === crumbs.length - 1 ? (
                      <Text>{c.label}</Text>
                    ) : (
                      <BreadcrumbLink as={Link} to={c.href}>{c.label}</BreadcrumbLink>
                    )}
                  </BreadcrumbItem>
                ))}
              </Breadcrumb>
            )}
          </Box>
        </HStack>

        <HStack spacing={2}>
          <IconButton 
            aria-label="Toggle color mode" 
            icon={colorMode === 'light' ? <Moon size={18} /> : <Sun size={18} />} 
            variant="ghost"
            onClick={toggleColorMode}
          />
          <IconButton aria-label="Notifications" icon={<Bell />} variant="ghost" />
          {userIsTeamLead && (
            <Menu>
              <MenuButton
                as={IconButton}
                aria-label="Team Overview"
                icon={<UserCheck size={18} />}
                variant="ghost"
              />
              <MenuList>
                <MenuItem icon={<UserCheck size={16} />} as={Link} to="/agent-status">
                  Agent Status
                </MenuItem>
              </MenuList>
            </Menu>
          )}
          <Menu>
            <MenuButton as={Button} variant="ghost" leftIcon={<Avatar size="sm" name={user?.user_login || ''} bg={avatarBgColor} color="white" />}>
              {user?.user_login ? user.user_login.split(' ')[0] : ''}
            </MenuButton>
            <MenuList>
              <Box px={2} py={2}>
                <Text fontSize="xs" fontWeight="600" textTransform="uppercase" letterSpacing="wider" color={colors.textSecondary} mb={2}>
                  User Status
                </Text>
                <VStack align="stretch" spacing={0}>
                  {[
                    { key: 'online' as const, label: 'Online', icon: Circle },
                    { key: 'break' as const, label: '1st/2nd Break', icon: Clock },
                    { key: 'lunch' as const, label: 'Lunch Break', icon: Coffee },
                  ].map(({ key, label, icon: Icon }) => (
                    <MenuItem
                      key={key}
                      onClick={() => {
                        setUserStatus(key, (error) => {
                          showError(error)
                        })
                      }}
                      bg={userStatus === key ? colors.sidebarActiveBg : 'transparent'}
                      color={userStatus === key ? colors.heading : colors.text}
                      icon={<Icon size={16} />}
                    >
                      {label}
                    </MenuItem>
                  ))}
                </VStack>
              </Box>
              <Divider />
              <MenuItem icon={<User />} as={Link} to="/profile">Profile</MenuItem>
              <Divider />
              <MenuItem icon={<LogOut />} onClick={() => setIsLogoutOpen(true)}>
                Logout
              </MenuItem>
            </MenuList>
          </Menu>
        </HStack>
      </Flex>

      <AlertDialog
        isOpen={isLogoutOpen}
        leastDestructiveRef={cancelRef}
        onClose={() => setIsLogoutOpen(false)}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Confirm Logout
            </AlertDialogHeader>

            <AlertDialogBody>
              Are you sure you want to logout? You will need to sign in again to access your account.
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={() => setIsLogoutOpen(false)}>
                Cancel
              </Button>
              <Button colorScheme="red" onClick={() => { setIsLogoutOpen(false); onLogout(); }} ml={3}>
                Logout
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  )
}

export default Header

